import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useCurrentInvoiceTask } from '@/providers/InvoiceTaskProvider';
import { useEffect, useState } from 'react';
import { useInvoiceTaskComments, useInvoiceTaskCommentsInvalidate } from '@/hooks/api/1950_invoice_task_comments';
import { useCreateInvoiceTaskComment } from '@/hooks/api/1960_create_invoice_task_comment';
import { Comments as _Comments, OnCreateCommentProps, OnDeleteCommentProps, OnToggleReactionProps, OnUpdateCommentProps } from '@/components/element/Comments';
import { useParams } from 'react-router-dom';
import { useSalesOrderMembers } from '@/hooks/api/1280_sales_order_members';
import { useInvoiceTaskCommentReaction } from '@/hooks/api/1961_invoice_task_comment_reactions';
import { MentionData } from '@draft-js-plugins/mention';
import { useSalesOrderTargetUsers } from '@/hooks/api/1231_sales_order_target_users';
import { useUpdateInvoiceTaskComment } from '@/hooks/api/1962_update_invoice_task_comment';
import { useDeleteInvoiceTaskComment } from '@/hooks/api/1963_delete_invoice_task_comment';

export const Comments = () => {
  const { _w_, _t_, me } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { invoiceTask: task } = useCurrentInvoiceTask();
  const [mentions, setMentions] = useState<MentionData[]>([]);
  const { data: comments } = useInvoiceTaskComments({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    task_id: task!.id,
  });
  const { mutate } = useCreateInvoiceTaskComment();
  const { mutate: updateMutate } = useUpdateInvoiceTaskComment();
  const { mutate: deleteMutate } = useDeleteInvoiceTaskComment();
  const { invalidate: invalidateComments } = useInvoiceTaskCommentsInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    task_id: task!.id,
  });
  const { mutate: reactionMutate } = useInvoiceTaskCommentReaction();
  const { data: members } = useSalesOrderTargetUsers({ identifier: _w_!.identifier, team_id: _t_!.id, id: Number(sales_order_id) });

  useEffect(() => {
    if (members) {
      setMentions(() =>
        members.map((d) => ({
          id: d.id,
          name: d.user_account.name,
          avatar: d.user_account.icon_file_url,
        }))
      );
    }
  }, [members]);

  const onCreateComment = ({ content, content_json, setComment, setError, parent_id }: OnCreateCommentProps) => {
    mutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        sales_order_id: Number(sales_order_id),
        task_id: task!.id,
        content: content!,
        content_json: content_json,
        parent_id: parent_id,
      },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onUpdateComment = ({ id, content, content_json, setComment, setError }: OnUpdateCommentProps) => {
    updateMutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        sales_order_id: Number(sales_order_id),
        task_id: task!.id,
        id: id,
        content: content!,
        content_json: content_json,
      },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onDeleteComment = ({ id, setComment, setError }: OnDeleteCommentProps) => {
    deleteMutate(
      {
        identifier: _w_!.identifier,
        team_id: _t_!.id,
        sales_order_id: Number(sales_order_id),
        task_id: task!.id,
        id: id,
      },
      {
        onSuccess: (res) => {
          setComment(res);
          invalidateComments();
        },
        onError: (error: any) => {
          setError(error);
        },
        onSettled: () => {},
      }
    );
  };

  const onToggleReaction = ({ comment_id, emoji_id, content, setComment, setError }: OnToggleReactionProps) => {
    reactionMutate(
      { identifier: _w_!.identifier, team_id: _t_!.id, sales_order_id: Number(sales_order_id), task_id: task!.id, id: comment_id, emoji_id, content },
      {
        onSuccess: (res) => {
          setComment(res);
        },
        onError: (error: any) => setError(error),
        onSettled: () => {},
      }
    );
  };

  if (mentions.length == 0) return <></>;

  return (
    <_Comments
      identifier={`invoice_${_w_!.identifier}_${_t_!.id}_${sales_order_id}_${task!.id}`}
      comments={comments}
      user={me!}
      mentions={mentions}
      onCreateComment={onCreateComment}
      onToggleReaction={onToggleReaction}
      onUpdateComment={onUpdateComment}
      onDeleteComment={onDeleteComment}
    />
  );
};
